import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { useCallback } from 'react';
import { getFormattedTime } from '@lmt/unicorn-shared/app/components/patients/common';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './IncompleteVisitsPanel.module.scss';

import SaveIcon from '@lmt/unicorn-shared/app/icons/small-tick.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/red-cross.svg?react';
import { QueryFactory } from '@lmt/shared-types';

export interface IncompleteVisitInfo {
  id: number;
  startDate: Date;
  patientId: number;
  patientName: string;
  caseId: number;
  subtenantCode: string;
  visitType: string;
}

export interface Props {
  visit: IncompleteVisitInfo;
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
}

export const IncompleteVisitItem: React.FC<Props> = (props) => {
  const { visit, openCaseVisitTab, openPatientTab } = props;
  const i18n = useScopedTranslation('patients.overview.visits');
  const modals = useModal();

  const cancelOrCompleteVisit = useCallback(async (isCancel?: boolean) => {
    const isConfirmed = await modals.showConfirm(
      isCancel
        ? {
            title: i18n.t('confirm_cancel_visit_title'),
            text: i18n.t('confirm_cancel_visit_text'),
            okButtonText: i18n.t('confirm_cancel_visit_button'),
          }
        : {
            title: i18n.t('confirm_complete_visit_title'),
            text: i18n.t('confirm_complete_visit_text'),
            okButtonText: i18n.t('confirm_complete_visit_button'),
            okButtonColor: ButtonColor.Secondary,
          },
    );

    if (isConfirmed) {
      try {
        if (isCancel) {
          await QueryFactory.AppointmentEventsClient.cancelVisit(visit.id);
        } else {
          await QueryFactory.AppointmentEventsClient.completeVisit(visit.id);
        }
      } catch (err) {
        await modals.showError({ text: convertToErrorString(err) });
      }
    }
  }, []);

  return (
    <div
      className={styles.visit}
      onClick={() => openCaseVisitTab(visit.patientId, visit.caseId, visit.id)}
    >
      <div className={styles.data}>
        <div className={styles.startTime}>
          {getFormattedTime(visit.startDate)}
        </div>
        <div className={styles.visitInfoColumn}>
          <div className={styles.patientContainer}>
            <div
              className={styles.patient}
              onClick={(e) => {
                e.stopPropagation();
                openPatientTab(visit.patientId);
              }}
            >
              <div title={visit.patientName} className={styles['patient-name']}>
                {visit.patientName}
              </div>
            </div>
            <div className={styles.subtenantCode}>{visit.subtenantCode}</div>
          </div>
          <div className={styles.visitType}>{visit.visitType}</div>
        </div>
      </div>
      <div className={styles.controls}>
        <UniLink
          icon={<CancelIcon />}
          title={i18n.t('cancel_button')}
          color={ButtonColor.Danger}
          onClick={(event) => {
            event.stopPropagation();
            cancelOrCompleteVisit(true);
          }}
          className={styles.link}
        />
        <UniLink
          icon={<SaveIcon />}
          title={i18n.t('complete_button')}
          color={ButtonColor.Secondary}
          className={styles.link}
          onClick={(event) => {
            event.stopPropagation();
            cancelOrCompleteVisit();
          }}
        />
      </div>
    </div>
  );
};
